import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Download,
  Loader2,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { jpgToPdf, JpgToPdfOptions } from "@/Api/pdfTools";
import { DropZone } from "./DropZone";
import { downloadBlob, formatBytes } from "./utils";

const MAX_IMAGES = 50;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

type Item = { id: number; file: File; preview: string };

const isJpg = (file: File) =>
  file.type === "image/jpeg" || /\.(jpe?g)$/i.test(file.name);

let nextId = 1;

export const JpgToPdf = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [options, setOptions] = useState<JpgToPdfOptions>({
    orientation: "auto",
    page_size: "a4",
    margin: "none",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ blob: Blob; pages: number } | null>(null);

  // Libera as miniaturas ao sair da tela
  useEffect(
    () => () => items.forEach((item) => URL.revokeObjectURL(item.preview)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const addFiles = (files: File[]) => {
    const tooBig = files.filter((f) => f.size > MAX_IMAGE_SIZE);
    if (tooBig.length) toast.error("Cada imagem deve ter no máximo 20 MB.");

    const accepted = files.filter((f) => f.size <= MAX_IMAGE_SIZE);
    const room = MAX_IMAGES - items.length;

    if (accepted.length > room) toast.error(`Selecione no máximo ${MAX_IMAGES} imagens.`);

    setItems((prev) => [
      ...prev,
      ...accepted.slice(0, Math.max(room, 0)).map((file) => ({
        id: nextId++,
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  };

  const remove = (id: number) =>
    setItems((prev) => {
      prev.filter((i) => i.id === id).forEach((i) => URL.revokeObjectURL(i.preview));
      return prev.filter((i) => i.id !== id);
    });

  const move = (index: number, direction: -1 | 1) =>
    setItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;

      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const reset = () => {
    items.forEach((item) => URL.revokeObjectURL(item.preview));
    setItems([]);
    setResult(null);
  };

  const handleConvert = async () => {
    setLoading(true);
    try {
      const response = await jpgToPdf(
        items.map((i) => i.file),
        options
      );

      if (response) setResult({ blob: response.blob, pages: items.length });
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="flex flex-col items-center gap-5 rounded-xl border bg-muted/20 px-6 py-12 text-center">
        <CheckCircle2 className="h-12 w-12 text-green-600" />
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">Seu PDF está pronto</h2>
          <p className="text-muted-foreground">
            {result.pages} {result.pages === 1 ? "imagem convertida" : "imagens convertidas"} em um único
            PDF ({formatBytes(result.blob.size)}).
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            size="lg"
            className={cn("h-12 px-8", colors.bg_primary)}
            onClick={() => downloadBlob(result.blob, "imagens.pdf")}
          >
            <Download className="mr-2 h-5 w-5" />
            Baixar PDF
          </Button>
          <Button size="lg" variant="outline" className="h-12" onClick={reset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Converter outras imagens
          </Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <DropZone
        title="JPG para PDF"
        description="Converta suas imagens JPG para PDF. Ajuste a orientação e as margens."
        buttonLabel="Selecionar imagens JPG"
        hint="ou arraste e solte as imagens JPG aqui"
        accept="image/jpeg,.jpg,.jpeg"
        multiple
        isValid={isJpg}
        invalidMessage="Selecione apenas imagens JPG."
        onInvalid={(message) => toast.error(message)}
        onFiles={addFiles}
      />
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4 rounded-xl border bg-muted/20 p-6">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold">
            {items.length} {items.length === 1 ? "imagem" : "imagens"}
          </h3>
          <label>
            <input
              type="file"
              accept="image/jpeg,.jpg,.jpeg"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                const valid = files.filter(isJpg);
                if (valid.length !== files.length) toast.error("Selecione apenas imagens JPG.");
                addFiles(valid);
                e.target.value = "";
              }}
            />
            <span className="inline-flex h-9 cursor-pointer items-center rounded-md border bg-background px-3 text-sm font-medium hover:bg-accent">
              <Plus className="mr-1 h-4 w-4" />
              Adicionar mais
            </span>
          </label>
        </div>
        <p className="text-sm text-muted-foreground">
          As páginas seguem a ordem da lista. Use as setas para reorganizar.
        </p>

        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item, index) => (
            <li key={item.id} className="overflow-hidden rounded-lg border bg-background">
              <div className="relative flex h-40 items-center justify-center bg-muted/40">
                <img src={item.preview} alt={item.file.name} className="max-h-full max-w-full object-contain" />
                <span className="absolute left-2 top-2 rounded bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
                  {index + 1}
                </span>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 top-2 h-7 w-7"
                  onClick={() => remove(item.id)}
                  disabled={loading}
                  title="Remover imagem"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2 p-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{item.file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(item.file.size)}</p>
                </div>
                <div className="flex shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || loading}
                    title="Mover para cima"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1 || loading}
                    title="Mover para baixo"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-4 rounded-xl border p-6">
        <h3 className="text-lg font-semibold">Opções do PDF</h3>

        <div className="space-y-2">
          <Label>Orientação</Label>
          <Select
            value={options.orientation}
            onValueChange={(v) => setOptions({ ...options, orientation: v as JpgToPdfOptions["orientation"] })}
            disabled={options.page_size === "image"}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Automática (conforme a imagem)</SelectItem>
              <SelectItem value="portrait">Retrato</SelectItem>
              <SelectItem value="landscape">Paisagem</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tamanho da página</Label>
          <Select
            value={options.page_size}
            onValueChange={(v) => setOptions({ ...options, page_size: v as JpgToPdfOptions["page_size"] })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a4">A4</SelectItem>
              <SelectItem value="image">Do tamanho da imagem</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Margem</Label>
          <Select
            value={options.margin}
            onValueChange={(v) => setOptions({ ...options, margin: v as JpgToPdfOptions["margin"] })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sem margem</SelectItem>
              <SelectItem value="small">Pequena</SelectItem>
              <SelectItem value="big">Grande</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          size="lg"
          className={cn("h-12 w-full", colors.bg_primary)}
          onClick={handleConvert}
          disabled={loading}
        >
          {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
          {loading ? "Convertendo..." : "Converter para PDF"}
        </Button>
        <Button variant="outline" className="w-full" onClick={reset} disabled={loading}>
          Limpar tudo
        </Button>
      </div>
    </div>
  );
};
